import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { STORAGE_KEYS } from '../common/constants';
import authService, { LoginCredentials, SignupData, User } from '../services/auth.service';

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    loading: boolean;
    error: string | null;

    // Actions
    login: (credentials: LoginCredentials) => Promise<void>;
    signup: (data: SignupData) => Promise<{ message: string; userId: string }>;
    logout: () => void;
    setUser: (user: User) => void;
    setToken: (token: string) => void;
    clearError: () => void;
    refreshUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            isAuthenticated: false,
            loading: false,
            error: null,

            login: async (credentials) => {
                set({ loading: true, error: null });
                try {
                    const { token, user } = await authService.login(credentials);
                    if (!token || !user) {
                        throw new Error('Login failed: invalid server response');
                    }
                    sessionStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
                    set({ user, token, isAuthenticated: true, loading: false });
                } catch (error: any) {
                    set({ error: error.message, loading: false });
                    throw error;
                }
            },

            // Signup does NOT authenticate: the backend requires email verification
            // before login, and only returns { message, userId }.
            signup: async (data) => {
                set({ loading: true, error: null });
                try {
                    const response = await authService.signup(data);
                    set({ loading: false });
                    return response;
                } catch (error: any) {
                    set({ error: error.message, loading: false });
                    throw error;
                }
            },

            logout: () => {
                sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
                sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
                set({ user: null, token: null, isAuthenticated: false, error: null });
            },

            setUser: (user) => {
                set({ user, isAuthenticated: true });
            },

            setToken: (token) => {
                sessionStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
                set({ token, isAuthenticated: true });
            },

            clearError: () => {
                set({ error: null });
            },

            refreshUser: async () => {
                const { token } = get();
                if (!token) return;

                try {
                    const user = await authService.getCurrentUser();
                    set({ user });
                } catch (error: any) {
                    console.error('Failed to refresh user:', error);
                    get().logout();
                }
            },
        }),
        {
            name: STORAGE_KEYS.USER_DATA,
            storage: createJSONStorage(() => sessionStorage),
            partialize: (state) => ({
                user: state.user,
                token: state.token,
                isAuthenticated: state.isAuthenticated,
            }),
            onRehydrateStorage: () => (state) => {
                if (state?.token) {
                    sessionStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, state.token);
                }
            },
        },
    ),
);
